import { useState, useEffect, useMemo, use, ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ReactSVG } from "react-svg";
import { FaRegTrashAlt, FaSpinner, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../../api/api";
import { IoIosArrowBack } from "react-icons/io";
import { useTranslation } from "react-i18next";
import { FaPencil } from "react-icons/fa6";
import { useProfileStore } from "../../store/useUserStore";
import { driver } from "driver.js";
import { motion, AnimatePresence } from "framer-motion";
import { TbBuildingBank } from "react-icons/tb";
import { formatTimeSlotList } from "../../helpers/helper";
import { FaHistory } from "react-icons/fa";
import { FiX } from "react-icons/fi";

interface Month {
  id: string;
  name: string;
  date: Date;
}

// ─── Cart line-item shared components ───────────────────────────────────────

/**
 * Outer flex row shared by all three cart item types.
 * Layout: [w-1/5 image] [flex-1 content] with an optional full-width footer.
 */
const CartItemRow = ({
  imageSrc,
  invalid,
  imageOverlay,
  footer,
  children,
}: {
  imageSrc?: string;
  invalid?: boolean;
  imageOverlay?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}) => (
  <div className={`mt-5${invalid ? " bg-gray-300" : ""}`}>
    <div className="flex gap-3">
      {/* Image column (w-1/5) */}
      <div className={`w-1/5 shrink-0 self-center${imageOverlay ? " relative" : ""}`}>
        {imageOverlay}
        <img
          src={imageSrc || "/assets/img/products/product1.png"}
          className="w-full aspect-square object-cover"
        />
      </div>
      {/* Content column (flex-1) */}
      <div className="flex-1 min-w-0">{children}</div>
    </div>
    {footer}
  </div>
);

/**
 * Detail / price row used by course and wave items.
 * Layout: [flex-1 detail text] [shrink-0 price]
 */
const CartPriceRow = ({
  detail,
  price,
  priceClassName,
}: {
  detail: ReactNode;
  price: ReactNode;
  priceClassName?: string;
}) => (
  <div className="flex justify-between gap-2 mt-2">
    <div className="flex-1 min-w-0 whitespace-normal break-words">{detail}</div>
    <div className={`shrink-0 text-end font-medium${priceClassName ? ` ${priceClassName}` : ""}`}>
      {price}
    </div>
  </div>
);

const Cart = () => {
  // let cartTotalPrice = 0;
  const [cartItems, setCartItems] = useState([]);
  const [academyBreakdown, setAcademyBreakdown] = useState<any[]>([]);
  const [innerLoading, setInnerLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [notificationCount, setNotificationCount] = useState(0);
  const {
    setHomePageShow,
    setSelectedStudent,
    setCartEdit,
    startProgress,
    tickProgress,
    selectedVouchers,
  } = useProfileStore();

  // State to manage timeslots schedules modal
  const [openScheduleModal, setOpenScheduleModal] = useState(false);
  const [selectedScheduleList, setSelectedScheduleList] = useState([]);

  const [showOverdueDeleteModel, setShowOverdueDeleteModel] = useState(false);
  const [overdueItem, setOverdueItem] = useState<any>({});
  const [whatsappNum, setWhatsappNum] = useState('');

  useEffect(() => {
    getCartItems();
    getOrderNotificationCount();
  }, []);

  function getOrderNotificationCount() {
    // get payment notification count
    api
      .get(`${process.env.PUBLIC_URL}/order/notification/summary`)
      .then((res) => {
        console.log("res notif", res);
        setNotificationCount(res.data.data.seen_count);
      })
      .catch((err) => {
        console.log("err", err);
      });
  }
  const [waivedSet, setWaivedSet] = useState<Set<string>>(new Set());

  function organizeCartData(data: any[]) {
    const organized: any[] = [];

    data.forEach((student) => {
      // Group cart items by branch
      const itemsMap: { [key: string]: any[] } = {};
      const contactMap: { [key: string]: string } = {};

      student.cart.forEach((item: any) => {
        const branchName = item.branch_name;
        const branchWhatsapp = item.branch_contact ?? '';

        if (!contactMap[branchName]) {
          contactMap[branchName] = branchWhatsapp;
        }

        if (!itemsMap[branchName]) {
          itemsMap[branchName] = [];
        }
        itemsMap[branchName].push(item);
      });

      // Convert branches map to array format
      const branches = Object.keys(itemsMap).map((branchName) => ({
        branch_name: branchName,
        branch_whatsapp: contactMap[branchName],
        items: itemsMap[branchName],
      }));

      // Add student with organized branches
      organized.push({
        student_id: student.student_id,
        name: student.student_name,
        branches: branches,
      });
    });

    console.log("organized cart data", organized);
    return organized;
  }

  // Usage in component:
  const [organizedCart, setOrganizedCart] = useState<any[]>([]);

  useEffect(() => {
    if (cartItems && cartItems.length > 0) {
      const organized = organizeCartData(cartItems);
      setOrganizedCart(organized);
    }
  }, [cartItems]);

  function getCartItems() {
    startProgress(1);
    api
      .get(`${process.env.REACT_APP_API_BASE_URL}/cart`)
      .then((res) => {
        console.log("res cart", res.data.data);
        setCartItems(res.data.data.students);

        // Filter out academy breakdown items with all zero discount values
        const academyBreakdownData =
          res.data.data?.discount_summary?.academy_breakdown;

        // Handle case where academy_breakdown might be an empty object {} instead of array
        let filteredAcademyBreakdown: any[] = [];
        if (Array.isArray(academyBreakdownData)) {
          filteredAcademyBreakdown = academyBreakdownData.filter(
            (academy: any) => {
              const waveDiscount = parseFloat(academy.wave_discount_total || 0);
              const bundleDiscount = parseFloat(
                academy.bundle_discount_total || 0,
              );
              const siblingDiscount = parseFloat(
                academy.sibling_discount_total || 0,
              );
              const batchDiscount = parseFloat(
                academy.batch_discount_total || 0,
              );

              // Keep academy if any discount is greater than 0
              return (
                bundleDiscount > 0 ||
                siblingDiscount > 0 ||
                batchDiscount > 0 ||
                waveDiscount > 0
              );
            },
          );
        }

        setAcademyBreakdown(filteredAcademyBreakdown);

        const newWaivedSet = new Set<string>();
        res.data.data.students.forEach((student) => {
          student.cart.forEach((item) => {
            if (item.type === "wave") {
              if (item.payment_method === "month") {
                const key = `${item.course_branch_id}_${item.selected_date}`;
                newWaivedSet.add(key);
              } else if (item.payment_method === "attandance") {
                const key = `${item.course_branch_id}_${item.selected_package.id}`;
                newWaivedSet.add(key);
              } else if (item.payment_method === "attandance-no replace") {
                const key = `${item.course_branch_id}_${item.selected_package.id}`;
                newWaivedSet.add(key);
              }
            }
          });
        });
        setWaivedSet(newWaivedSet);
      })
      .catch((err) => {
        toast.error(t("server_error"));
        console.log("err cart", err);
      })
      .finally(() => {
        tickProgress();
        setIsLoading(false);
      });
  }

  function deleteCartItem(cartItem: any) {
    console.log("deleteCartItem");
    setInnerLoading(true);
    api
      .delete(`${process.env.PUBLIC_URL}/cart/delete/${cartItem.id}`)
      .then((res) => {
        setInnerLoading(false);
        toast.success("Delete from cart");
        console.log("res", res);
        getCartItems();
      })
      .catch((err) => {
        setInnerLoading(false);
        toast.error(t("server_error"));
        console.log("err", err);
      });
  }

  function handleCheckout() {
    console.log("handleCheckout");
    startProgress(1);
    api
      .post(`${process.env.PUBLIC_URL}/cart/checkout`, {
        final_price: cartTotalPrice.toFixed(2),
      })
      .then((res) => {
        console.log("res", res);

        if (res.data.skip_payment === true) {
          toast.success(t("cart.payment_success"));
          getCartItems();
          return;
        }

        const paymentUrl = res.data.payment_url;
        if (paymentUrl) {
          window.location.href = paymentUrl;
        } else {
          toast.error(t("url_not_found"));
        }
      })
      .catch((error) => {
        // Show API error message if available
        if (
          error.response &&
          error.response.data &&
          error.response.data.errors
        ) {
          const errors = error.response.data.errors;

          // Handle non_field_errors
          if (
            errors.non_field_errors &&
            Array.isArray(errors.non_field_errors)
          ) {
            errors.non_field_errors.forEach((errorMessage: string) => {
              toast.error(errorMessage);
            });
          } else {
            // Handle other field-specific errors
            Object.keys(errors).forEach((field) => {
              if (Array.isArray(errors[field])) {
                errors[field].forEach((errorMessage: string) => {
                  toast.error(`${field}: ${errorMessage}`);
                });
              }
            });
          }
        } else if (
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          toast.error(error.response.data.message);
        } else if (error.response?.data?.error) {
          toast.error(error.response.data.error);
        } else {
          // Fallback error message
          toast.error("An unexpected error occurred.");
        }
        console.log("err", error);
      })
      .finally(() => {
        tickProgress();
      });
  }

  const [showMonthModal, setShowMonthModal] = useState(false);
  const [selectedCartItem, setSelectedCartItem] = useState<any>(null);
  const [selectedMonths, setSelectedMonths] = useState<string[]>([]);

  const getMonths = (): Month[] => {
    const months: Month[] = [];

    const startDate = new Date();
    startDate.setDate(1); // Start from the first day of the current month
    startDate.setMonth(startDate.getMonth() - 6); // Move 6 months back

    for (let i = 0; i < 24; i++) {
      // Total 24 months (6 past + current + 17 future)
      const month = new Date(startDate);
      month.setMonth(startDate.getMonth() + i);

      const year = month.getFullYear();
      const monthNumber = String(month.getMonth() + 1).padStart(2, "0");

      months.push({
        id: `${year}-${monthNumber}`,
        name: month.toLocaleString("default", {
          month: "short",
          year: "numeric",
        }),
        date: month,
      });
    }

    return months;
  };

  const months: Month[] = getMonths();

  const handleSelectMonth = (month: Month) => {
    setSelectedMonths((prev: any) =>
      prev.includes(month.id)
        ? prev.filter((id: any) => id !== month.id)
        : [...prev, month.id],
    );
  };
  const isChecked = (month: Month) => {
    return selectedMonths ? selectedMonths.includes(month.id) : false;
  };

  useEffect(() => {
    // Parse "Oct 2024" into a Date object (if selected_date exists)
    if (selectedCartItem?.selected_date) {
      const parsed = new Date(`${selectedCartItem.selected_date} 01`);
      const year = parsed.getFullYear();
      const month = String(parsed.getMonth() + 1).padStart(2, "0");
      const selectedId = `${year}-${month}`;

      setSelectedMonths((prev: string[]) => {
        if (!prev.includes(selectedId)) {
          return [...prev, selectedId];
        }
        return prev;
      });
    }
  }, [selectedCartItem]);

  function navigateCourseView(
    student_id: number,
    student_name: string,
    course_branch_id: number,
  ) {
    setHomePageShow("school");
    setSelectedStudent({ id: student_id, name: student_name });
    // setLastVisitedCourseId(course_branch_id.toString())
    return navigate(`/course/${course_branch_id}?from=cart`);
  }

  function navigateItemView(
    student_id: number,
    student_name: string,
    item_id: number,
    course_branch_id: number,
  ) {
    setHomePageShow("school");
    setSelectedStudent({ id: student_id, name: student_name });
    return navigate(
      `/item/${item_id}?course_branch_id=${course_branch_id}&from=cart`,
    );
  }

  const cartSubtotal = useMemo(() => {
    const courseGroups = new Map<
      string,
      {
        total: number;
        remaining: number;
        course_branch_id: number;
        selected_package_id?: number;
        selected_date?: string;
      }
    >();

    // 1. Group all course items
    cartItems.forEach((student: any) => {
      student.cart.forEach((item: any) => {
        if (item.type === "course") {
          const isMonth = item.pricing_method?.toLowerCase() === "month";
          const key = isMonth
            ? `${item.course_branch_id}`
            : `${item.course_branch_id}_${item.selected_package?.id}`;

          const itemPrice = parseFloat(item.price) || 0;
          if (!courseGroups.has(key)) {
            courseGroups.set(key, {
              total: itemPrice,
              remaining: itemPrice,
              course_branch_id: item.course_branch_id,
              selected_package_id: item.selected_package?.id,
              selected_date: item.selected_date,
            });
          } else {
            const existing = courseGroups.get(key)!;
            existing.total += itemPrice;
            existing.remaining += itemPrice;
          }
        }
      });
    });

    // 2. Deduct wave items
    cartItems.forEach((student: any) => {
      student.cart.forEach((item: any) => {
        if (item.type === "wave") {
          const isMonth = item.payment_method === "month";
          const key = isMonth
            ? `${item.course_branch_id}`
            : `${item.course_branch_id}_${item.selected_package?.id}`;

          const group = courseGroups.get(key);
          if (group) {
            const itemPrice = parseFloat(item.price) || 0;
            const deduction = Math.min(itemPrice, group.remaining);
            group.remaining -= deduction;
          }
        }
      });
    });

    // 3. Calculate subtotal (before academy discounts)
    let subtotal = 0;
    Array.from(courseGroups.values()).forEach((group) => {
      subtotal += group.remaining;
    });
    // 4. Add item type "item" price (not waived)
    cartItems.forEach((student: any) => {
      student.cart.forEach((item: any) => {
        if (item.type === "item") {
          const itemPrice = parseFloat(item.item?.price) || 0;
          const quantity = parseInt(item.quantity) || 1;
          subtotal += itemPrice * quantity;
        }
      });
    });

    console.log("cart subtotal", subtotal);

    return subtotal;
  }, [cartItems]);

  const cartTotalPrice = useMemo(() => {
    const academyDiscountTotal = academyBreakdown.reduce((total, academy) => {
      const bundleDiscount = parseFloat(academy.bundle_discount_total || 0);
      const siblingDiscount = parseFloat(academy.sibling_discount_total || 0);
      const batchDiscount = parseFloat(academy.batch_discount_total || 0);

      return total + bundleDiscount + siblingDiscount + batchDiscount;
    }, 0);

    console.log("academyDiscountTotal,", academyDiscountTotal);

    return Math.max(cartSubtotal - academyDiscountTotal, 0);
  }, [cartSubtotal, academyBreakdown]);

  return (
    <div className="pb-20">
      <div className="mb-2 flex items-center justify-between pl-2 pr-3">
        <IoIosArrowBack size={28} color="#000" onClick={() => navigate("/")} />
        <div className="text-lg font-medium flex-1 text-center">
          {t("cart.cart")}
        </div>
        <div className="relative">
          <FaHistory
            id="payment-history-icon"
            onClick={() => navigate("/payment-history")}
            className="ml-auto cursor-pointer w-6 h-6"
          />

          {/* Red Notification Badge */}
          {notificationCount > 0 && (
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full"></span>
          )}
        </div>
      </div>

      <div className="flex">
        {/* My Active Course Button */}
        <Link to="/active-course" className="w-full mb-4 mt-8 px-3">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 flex items-center justify-between hover:shadow-md transition-shadow">
            <span className="text-lg font-semibold text-gray-900">
              My Course
            </span>
            <svg
              className="w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </Link>
        <Link to="/pre-enroll" className="w-full mb-4 mt-8 px-3">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 flex items-center justify-between hover:shadow-md transition-shadow">
            <span className="text-lg font-semibold text-gray-900">
              {t("course_view.pre_enrol")}
            </span>
            <svg
              className="w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </Link>
      </div>

      <div className="px-3">
        {!isLoading && cartItems && cartItems.length >= 1 ? (
          <>
            {organizedCart.map((student, studentIndex) => (
              <div key={studentIndex} className="border-gray-300 pb-8">
                <div className="text-lg font-semibold mt-3">{student.name}</div>
                {student.branches.map((branch: any, branchIndex: number) => (
                  <div key={branchIndex}>
                    <div className="flex items-center bg-blue-100 rounded-lg px-3 py-1 my-3">
                      <TbBuildingBank className="mr-2" />
                      <span className="font-medium text-sm text-gray-700">
                        {branch.branch_name}
                      </span>
                    </div>
                    {branch.items.map((item: any, index: number) => {
                      if (item.type === "course") {
                        return (
                          <CartItemRow
                            key={index}
                            imageSrc={item.course.images?.[0]}
                            invalid={!item.validity.is_valid}
                            imageOverlay={
                              item.applied_discount_tags?.length > 0 ? (
                                <div className="absolute top-0 left-0 z-10 flex flex-col gap-1">
                                  {item.applied_discount_tags.map(
                                    (tag: any, tagIndex: number) => (
                                      <div
                                        key={tagIndex}
                                        className={`text-white px-2 py-1 text-xs font-semibold rounded ${
                                          tag.type === "bundle"
                                            ? "bg-orange-400"
                                            : tag.type === "sibling"
                                            ? "bg-red-500"
                                            : ""
                                        }`}
                                      >
                                        {tag.type === "bundle"
                                          ? t("promotion.bundle")
                                          : tag.type === "sibling"
                                          ? t("promotion.sibling")
                                          : ""}
                                      </div>
                                    ),
                                  )}
                                </div>
                              ) : undefined
                            }
                          >
                            {/* Title row: title (flex-1) · status + actions (shrink-0) */}
                            <div className="flex justify-between gap-1">
                              <div className="flex-1 min-w-0">
                                <div
                                  onClick={() =>
                                    navigateCourseView(
                                      student.student_id,
                                      student.name,
                                      item.course_branch_id,
                                    )
                                  }
                                  className={`text-lg font-medium truncate ${item.validity.is_valid ? "" : "opacity-25"}`}
                                >
                                  {item.course.name}
                                </div>
                                {item.validity.is_valid ? (
                                  <div
                                    className="truncate"
                                    onClick={() => {
                                      setSelectedScheduleList(item.schedules);
                                      setOpenScheduleModal(true);
                                    }}
                                  >
                                    {formatTimeSlotList(item.schedules)}
                                  </div>
                                ) : (
                                  <div className="h-5" />
                                )}
                              </div>
                              <div className="flex flex-col shrink-0 items-end">
                                {item.validity.is_valid ? (
                                  <div
                                    className={`rounded-xl text-center p-[0.12rem] px-2 text-xs truncate ${
                                      item.status === "added"
                                        ? "bg-green-500 text-white"
                                        : item.status === "overdue"
                                        ? "bg-red-500 text-white"
                                        : item.status === "pre_enroll"
                                        ? "text-white bg-[#023f81]"
                                        : "bg-amber-600 text-white"
                                    }`}
                                  >
                                    {item.status === "added"
                                      ? t("cart.newly_added")
                                      : item.status === "overdue"
                                      ? t("cart.overdue")
                                      : item.status === "pre_enroll"
                                      ? t("course_view.pre_enrol")
                                      : t("cart.due_soon")}
                                  </div>
                                ) : (
                                  <div className="h-5" />
                                )}
                                <div className="flex gap-2 items-center mt-2">
                                  {item.status !== "overdue" && item.validity.is_valid && (
                                    <FaPencil
                                      className="cursor-pointer"
                                      onClick={() => {
                                        setCartEdit({
                                          ...item,
                                          student_id: student.student_id,
                                          student_name: student.name,
                                        });
                                        return navigate(
                                          `/course/${item.course_branch_id}/edit`,
                                        );
                                      }}
                                    />
                                  )}
                                  <FaRegTrashAlt
                                    size={16}
                                    className="mt-1 cursor-pointer"
                                    onClick={() => {
                                      if (item.status === "overdue") {
                                        setShowOverdueDeleteModel(true);
                                        setWhatsappNum(branch.branch_whatsapp);
                                        setOverdueItem(item);
                                      } else {
                                        deleteCartItem(item);
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                            {/* Detail / price row */}
                            <CartPriceRow
                              detail={
                                item.validity.is_valid ? (
                                  <div className="flex gap-2 items-center">
                                    {item.pricing_method === "Month"
                                      ? item.selected_date
                                      : item.selected_package.count + " attd"}
                                    {item.selected_package.description &&
                                    item.selected_package.description !== "None"
                                      ? ` (${item.selected_package.description})`
                                      : ""}
                                  </div>
                                ) : null
                              }
                              price={`RM ${item.price}`}
                            />
                          </CartItemRow>
                        );
                      } else if (item.type === "wave") {
                        const waiveBalancePrice =
                          item.payment_method === "month"
                            ? item.price
                            : item.price || 0;

                        return (
                          <CartItemRow
                            key={index}
                            imageSrc={item.course.images?.[0]}
                            footer={
                              <div className="mt-2 p-2 border border-1 border-gray-300 rounded">
                                <span className="text-theme">{item.remark}</span>
                              </div>
                            }
                          >
                            {/* Title */}
                            <div>
                              <div
                                onClick={() =>
                                  navigateCourseView(
                                    student.student_id,
                                    student.student_name,
                                    item.course_branch_id,
                                  )
                                }
                                className="text-lg font-medium truncate"
                              >
                                Waive Payment
                              </div>
                              <div>{item.course.name}</div>
                            </div>
                            {/* Detail / price row */}
                            <CartPriceRow
                              detail={item.selected_date}
                              price={`RM ${waiveBalancePrice || 0}`}
                              priceClassName="text-red-600"
                            />
                          </CartItemRow>
                        );
                      } else if (item.type === "item") {
                        // cartTotalPrice += cartItem.item.price;
                        return (
                          <CartItemRow key={index} imageSrc={item.item.images?.[0]}>
                            {/* Title row: title (flex-1) · delete action (shrink-0) */}
                            <div className="flex justify-between gap-1">
                              <div className="flex-1 min-w-0">
                                <div
                                  className="text-lg font-medium truncate"
                                  onClick={() =>
                                    navigateItemView(
                                      student.student_id,
                                      student.name,
                                      item.item.id,
                                      item.item.course_branch_id,
                                    )
                                  }
                                >
                                  {item.item.name}
                                </div>
                              </div>
                              <div className="shrink-0">
                                {item.deletable &&
                                  (innerLoading ? (
                                    <FaSpinner className="animate-spin" />
                                  ) : (
                                    <FaRegTrashAlt
                                      size={18}
                                      className="mt-1"
                                      onClick={() => {
                                        deleteCartItem(item);
                                      }}
                                    />
                                  ))}
                              </div>
                            </div>
                            {/* Quantity and price */}
                            <div className="mt-2">
                              <div className="whitespace-normal break-words">
                                {t("cart.quantity")}: {item.quantity}
                              </div>
                              <div className="text-end text-lg font-medium">{`RM ${
                                item.item.price * (item.quantity || 1)
                              }`}</div>
                            </div>
                          </CartItemRow>
                        );
                      }
                    })}
                  </div>
                ))}
              </div>
            ))}

            <div className="pb-8 pt-4 border-t border-gray-300">
              <div className="max-w-screen-md mx-auto">
                {/* Voucher/Discount Section */}

                {/* Subtotal */}
                <div className="flex justify-between items-center mb-3">
                  <span className="text-base">{t("cart.sub_total")}</span>
                  <span className="text-base">
                    RM {cartSubtotal.toFixed(2)}
                  </span>
                </div>

                {/* Academy Breakdown */}
                {academyBreakdown && academyBreakdown.length > 0 && (
                  <div className="border border-gray-200 rounded-lg p-4 mb-4">
                    {academyBreakdown.map((academy, index) => (
                      <div key={academy.academy_id}>
                        {/* Academy Name */}
                        <div className="font-medium text-gray-800 mb-2">
                          {academy.academy_name}
                        </div>

                        {/* Sibling Discount */}
                        {parseFloat(academy.sibling_discount_total) > 0 && (
                          <div className="flex justify-between items-center text-sm mb-1">
                            <span className="text-red-500">
                              {t("promotion.sibling_discount")}
                            </span>
                            <span className="text-red-500">
                              - RM{" "}
                              {parseFloat(
                                academy.sibling_discount_total,
                              ).toFixed(2)}
                            </span>
                          </div>
                        )}

                        {/* Bundle Discount */}
                        {parseFloat(academy.bundle_discount_total) > 0 && (
                          <div className="flex justify-between items-center text-sm mb-1">
                            <span className="text-orange-500">
                              {t("promotion.bundle_discount")}
                            </span>
                            <span className="text-orange-500">
                              - RM{" "}
                              {parseFloat(
                                academy.bundle_discount_total,
                              ).toFixed(2)}
                            </span>
                          </div>
                        )}

                        {parseFloat(academy.batch_discount_total) > 0 && (
                          <div className="flex justify-between items-center text-sm mb-1">
                            <span className="text-blue-500">
                              {t("promotion.batch_discount")}
                            </span>
                            <span className="text-blue-500">
                              - RM{" "}
                              {parseFloat(academy.batch_discount_total).toFixed(
                                2,
                              )}
                            </span>
                          </div>
                        )}

                        {/* Add spacing between academies if not the last one */}
                        {index < academyBreakdown.length - 1 && (
                          <div className="my-3"></div>
                        )}
                      </div>
                    ))}

                    {/* Total Saved */}
                    <div className="border-t border-gray-300 pt-3 mt-3">
                      <div className="flex justify-between items-center font-medium">
                        <span className="text-gray-700">
                          {t("cart.total_saved")}
                        </span>
                        <span className="text-gray-700">
                          - RM{" "}
                          {academyBreakdown
                            .reduce((total, academy) => {
                              const bundleDiscount = parseFloat(
                                academy.bundle_discount_total || 0,
                              );
                              const siblingDiscount = parseFloat(
                                academy.sibling_discount_total || 0,
                              );
                              const batchDiscount = parseFloat(
                                academy.batch_discount_total || 0,
                              );
                              return (
                                total +
                                bundleDiscount +
                                siblingDiscount +
                                batchDiscount
                              );
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Divider */}
                <div className="border-t border-gray-200 pt-4">
                  {/* Grand Total */}
                  <div className="flex justify-between items-center mb-6">
                    <span className="text-lg font-semibold">
                      {t("cart.grand_total")}
                    </span>
                    <span className="text-lg font-semibold">
                      RM {cartTotalPrice.toFixed(2)}
                    </span>
                  </div>
                </div>

                <button
                  onClick={handleCheckout}
                  type="button"
                  className="text-white text-[16px] leading-none w-full py-[15px] bg-theme border-none rounded-3xl flex items-center justify-center"
                >
                  {innerLoading ? (
                    <FaSpinner className="animate-spin" />
                  ) : (
                    t("cart.proceed_to_checkout")
                  )}
                </button>
              </div>
            </div>
          </>
        ) : !isLoading ? (
          <div className="flex flex-col items-center justify-center text-center mt-52">
            <div className="flex items-center justify-center w-[100px] h-[70px] mb-5">
              <ReactSVG
                src={process.env.PUBLIC_URL + "/assets/img/icons/cart.svg"}
              />
            </div>
            <div>
              <p>
                {t("cart.no_items_in_cart")}{" "}
                <Link
                  to={process.env.PUBLIC_URL + "/"}
                  className="block underline font-medium"
                >
                  {t("cart.add_some")}
                </Link>
              </p>
            </div>
          </div>
        ) : null}
      </div>

      {/* Overdue Delete Modal */}
      {showOverdueDeleteModel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-80">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 ">
                <button
                    onClick={() => setShowOverdueDeleteModel(false)}
                    className="text-gray-500 hover:text-gray-700 text-xl font-bold"
                >
                    ×
                </button>
                <h3 className="text-lg font-semibold text-gray-900">{"Oh no!"}</h3>
                <div></div>
            </div>
            <div className="flex flex-col items-center gap-2 p-2">
              <div>
                <p className="text-sm text-gray-500 text-center">
                  <span>{"This is an "}</span>
                  <span className="text-red-500 font-medium">{"Overdue"}</span>
                  <span>{" payment, you need to contact the academy to remove it."}</span>
                  </p>
                  
              </div>

              <div className="p-4 grid grid-cols-5 gap-3 mt-5" >
                <div className="col-span-1 self-center relative">

                  {/* Image */}
                  {overdueItem.course.images &&
                  overdueItem.course.images.length > 0 ? (
                    <img
                      src={overdueItem.course.images[0]}
                      className="w-full aspect-square object-cover"
                    />
                  ) : (
                    <img
                      src={"/assets/img/products/product1.png"}
                      className="w-full aspect-square object-cover"
                    />
                  )}
                </div>
                <div className="col-span-4">
                  <div className="grid grid-cols-3 gap-1">
                    <div className="col-span-2">
                      <p className="text-sm font-medium "
                      >
                        {overdueItem.course.name}
                      </p>
                    </div>

                    <div className="flex flex-col col-span-1">
                      {overdueItem.validity.is_valid ? (
                        <div className="self-end">
                          <div
                            className="rounded-xl text-center p-[0.12rem] px-2 text-xs truncate bg-red-500 text-white"
                          > {t("cart.overdue")} </div>
                        </div>
                      ): <div className="h-5"></div>}
                    </div>
                    
                  </div>
                  
                  <div className="grid grid-cols-8 mt-2">
                    <div className="col-span-5"></div>
                    <div className=" col-span-3 text-end text-sm font-medium">{`RM ${overdueItem.price}`}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 border-t">
              <div
                title={!overdueItem.branch_contact ? "" : undefined}
                className="mx-auto w-fit"
              >
              <button
                  className={`${!overdueItem.branch_contact ? "bg-gray-300" : "bg-theme"} text-white px-8 py-3 rounded-full flex items-center justify-center transition-colors duration-200 shadow-md `}
                  onClick={!overdueItem.branch_contact ? () => {
                    toast.error("Sorry, No phone number added for this academy");
                  } : () => {
                    console.log("overdue item", overdueItem);
                      const message = encodeURIComponent(
                        `Hi, I am parent of student name, I would like to request to remove the Overdue payment of\nBranch: ${overdueItem.branch_name}\nCourse: ${overdueItem.course.name}\nTimeslot: ${formatTimeSlotList(overdueItem.schedules)}\nTotal: RM ${overdueItem.price}\n\nBecause of...`
                      )
                      const whatsappUrl = `https://api.whatsapp.com/send?phone=${overdueItem.branch_contact}&text=${message}`
                      window.open(whatsappUrl, '_blank');
                  }}
              >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                  </svg>
                  {"Contact Academy"}
              </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showMonthModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white px-6 py-2 rounded-lg w-5/6 max-w-md min-h-[300px] flex flex-col justify-between">
            <div>
              <div className="text-lg font-semibold mb-4">
                {t("cart.edit_payment_term")}
              </div>
              <div
                className="mt-2 bg-white rounded-md w-full z-10"
                style={{ maxHeight: "13rem", overflowY: "auto" }}
              >
                {months.map((month) => (
                  <div
                    key={month.id}
                    className={`flex items-center px-1 py-1 hover:bg-gray-100 cursor-pointer`}
                    onClick={() => {
                      handleSelectMonth(month);
                    }}
                  >
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={isChecked(month)}
                      readOnly
                    />
                    <span className="text-lg">{month.name}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowMonthModal(false);
                  setSelectedMonths([]);
                }}
                className="px-4 py-1 text-gray-700 rounded-full hover:bg-gray-100 transition"
              >
                {t("close")}
              </button>
              <button
                className="px-4 py-1 bg-theme text-white rounded-full transition"
                onClick={() => {
                  setShowMonthModal(false);
                  setSelectedMonths([]);
                }}
              >
                {t("save")}
              </button>
            </div>
          </div>
        </div>
      )}

      <AnimatePresence>
        {openScheduleModal && (
          <div
            className="fixed inset-0 z-10 bg-black bg-opacity-50 flex justify-center items-end"
            onClick={() => {
              setOpenScheduleModal(false);
              setSelectedScheduleList([]);
            }}
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ stiffness: 100 }}
              className="w-full max-w-[648px] bg-white shadow-2xl rounded-t-3xl px-4 pt-4 pb-20"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-10">
                {selectedScheduleList.map((slot: any, index) => {
                  const format12 = (time: string) => {
                    const [h, m] = time.split(":").map(Number);
                    const d = new Date();
                    d.setHours(h, m);
                    return d.toLocaleTimeString("en-US", {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    });
                  };

                  return (
                    <div key={index}>
                      {format12(slot?.start_time)} - {format12(slot?.end_time)}{" "}
                      {slot.day}
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Cart;
